import React, { useEffect, useMemo, useState } from 'react';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import StatusBanner from '../../components/ui/StatusBanner';
import EmptyState from '../../components/ui/EmptyState';
import {
  EmployeeAiPreparation,
  EmployeeDashboardGrid,
  EmployeeDetailList,
  EmployeeSummaryCard,
  EmployeeWorkspaceEmptyState,
  EmployeeWorkspaceSection
} from '../../components/workforce/EmployeeWorkspace';
import { apiRequest } from '../../services/apiClient';
import * as coachingApi from '../../services/coachingApi';
import { formatRoleLabel } from '../../config/appRoutes';

function displayName(person) {
  return person?.preferredName
    || [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim()
    || null;
}

function formatDate(value) {
  return value ? String(value).slice(0, 10) : null;
}

function activeDocuments(documents) {
  return (Array.isArray(documents) ? documents : []).filter(document => !document.archivedAt);
}

function AsyncActivity({ state, emptyTitle, emptyBody, children }) {
  if (state.status === 'loading') return <LoadingSkeleton variant="paragraph" lines={3} />;
  if (state.status === 'error') return <StatusBanner kind="error" message={state.error} />;
  if (!state.items.length) {
    return <EmptyState compact icon="·" headline={emptyTitle} body={emptyBody} />;
  }
  return children;
}

export default function EmployeeOverviewDashboard({
  profile,
  manager,
  supervisorLabel,
  documents,
  canReadDocuments,
  canViewCoaching,
  attendanceEnabled,
  summaryContent = null
}) {
  const [attendance, setAttendance] = useState({ status: attendanceEnabled ? 'loading' : 'idle', items: [], error: '' });
  const [coaching, setCoaching] = useState({ status: canViewCoaching ? 'loading' : 'idle', items: [], error: '' });
  const docs = useMemo(() => activeDocuments(documents), [documents]);
  const certifications = useMemo(() => docs.filter(doc => doc.documentType === 'certification'), [docs]);

  useEffect(() => {
    if (!attendanceEnabled || !profile?.id) return undefined;
    let cancelled = false;
    apiRequest(`/api/employees/profiles/${profile.id}/attendance`)
      .then(data => {
        if (!cancelled) setAttendance({ status: 'loaded', items: data?.summary ? [data.summary] : [], error: '' });
      })
      .catch(error => {
        if (!cancelled) setAttendance({ status: 'error', items: [], error: error?.message || 'Unable to load attendance summary.' });
      });
    return () => { cancelled = true; };
  }, [attendanceEnabled, profile?.id]);

  useEffect(() => {
    if (!canViewCoaching || !profile?.id) return undefined;
    let cancelled = false;
    coachingApi.listNotesForSubject({ employeeProfileId: profile.id, managerId: profile.managerId, limit: 3 })
      .then(items => { if (!cancelled) setCoaching({ status: 'loaded', items, error: '' }); })
      .catch(error => {
        if (!cancelled) setCoaching({ status: 'error', items: [], error: error?.message || 'Unable to load coaching activity.' });
      });
    return () => { cancelled = true; };
  }, [canViewCoaching, profile?.id, profile?.managerId]);

  const employmentStatus = profile.archivedAt ? 'Inactive' : 'Active';
  const managerName = displayName(manager);
  const location = manager?.locationCode || null;
  const attendanceRow = attendance.items[0] || null;

  return (
    <div className="employee-overview" data-testid="employee-overview-dashboard">
      <EmployeeWorkspaceSection title="Employee summary" description="Current identity and employment record">
        {summaryContent || (
          <EmployeeDetailList items={[
            { label: 'Name', value: displayName(profile) },
            { label: 'Employee ID', value: profile.employeeNumber },
            { label: 'Title', value: profile.jobTitle },
            { label: 'Department', value: profile.department },
            { label: 'Manager', value: supervisorLabel },
            { label: 'Location', value: location },
            { label: 'Hire date', value: formatDate(profile.dateOfHire) },
            { label: 'Employment status', value: employmentStatus }
          ]} />
        )}
      </EmployeeWorkspaceSection>

      <EmployeeDashboardGrid>
        <EmployeeWorkspaceSection title="Organization" description="Current reporting and operating context">
          <EmployeeDetailList items={[
            { label: 'Direct team', value: null },
            { label: 'Reporting structure', value: profile.supervisorEmployeeId ? `Employee profile #${profile.supervisorEmployeeId}` : null },
            { label: 'Business unit', value: null },
            { label: 'Operational role', value: manager?.managerRoleKey ? formatRoleLabel(manager.managerRoleKey) : null },
            { label: 'Location', value: location },
            { label: 'Roster identity', value: managerName || (profile.managerId ? `Roster record #${profile.managerId}` : null) }
          ]} />
        </EmployeeWorkspaceSection>

        <EmployeeWorkspaceSection title="Current activity" description="Live employee-scoped records">
          <EmployeeDashboardGrid columns="wide">
            <EmployeeSummaryCard label="Documents" value={canReadDocuments ? docs.length : '—'} detail={canReadDocuments ? (docs.length ? 'Active documents on file' : 'No documents recorded') : 'Document access restricted'} />
            <EmployeeSummaryCard label="Certifications" value={canReadDocuments ? certifications.length : '—'} detail={canReadDocuments ? (certifications.length ? 'Current records on file' : 'No certifications available') : 'Certification access restricted'} />
            <EmployeeSummaryCard label="Attendance" value={attendanceRow?.alertLevel || (attendanceEnabled ? '—' : 'Not enabled')} detail={attendanceRow ? `${attendanceRow.totalIncidents30 || 0} incidents in 30 days` : 'No attendance summary available'} tone={attendanceRow?.alertLevel === 'Critical' ? 'warning' : 'neutral'} />
          </EmployeeDashboardGrid>
          {canViewCoaching ? (
            <div className="employee-overview__activity-block">
              <h3>Recent coaching</h3>
              <AsyncActivity state={coaching} emptyTitle="No coaching sessions recorded" emptyBody="Leadership notes will appear here after they are recorded.">
                <ul className="employee-overview__activity-list">
                  {coaching.items.map(note => <li key={note.id}>{note.noteText || note.body}</li>)}
                </ul>
              </AsyncActivity>
            </div>
          ) : null}
        </EmployeeWorkspaceSection>
      </EmployeeDashboardGrid>

      <EmployeeDashboardGrid>
        <EmployeeWorkspaceSection title="Operational snapshot" description="Future capability">
          <EmployeeWorkspaceEmptyState title="Operational snapshot is planned" description="Employee-level operational measures are not available in the current data model." />
        </EmployeeWorkspaceSection>
        <EmployeeWorkspaceSection title="Performance snapshot" description="Future capability">
          <EmployeeWorkspaceEmptyState title="Performance snapshot is planned" description="No employee-level performance metrics are available in the current data model." />
        </EmployeeWorkspaceSection>
      </EmployeeDashboardGrid>

      <EmployeeWorkspaceSection title="Goals" description="Future employee-level connection">
        <EmployeeWorkspaceEmptyState title="No employee goals available" description="Goals are currently owned and allocated through the location and manager goal workflow." />
      </EmployeeWorkspaceSection>

      <EmployeeAiPreparation />
    </div>
  );
}
